import { useModalState, Text } from "@src/lib/ui-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  Skeleton,
  Stack,
} from "@mui/material";
import { isDefined } from "@clipboard-health/util-ts";
import type { Dispatch } from "redux";
import { useGetFacilities } from "./useGetFacilities";
import { Close as CloseIcon } from "@mui/icons-material";

interface WorkplaceSummary {
  userId: string;
  name: string;
  salesforceID?: string;
}

interface CorporateFacilitiesProps {
  currentWorkplace: WorkplaceSummary;
  parentWorkplace?: WorkplaceSummary;
  switchToUserSession: (data: { userId: string }) => (dispatch: Dispatch) => void;
}

export function CorporateFacilities(props: CorporateFacilitiesProps) {
  const { switchToUserSession, parentWorkplace, currentWorkplace } = props;
  const corporateModalState = useModalState();

  const {
    data: siblingWorkplaces,
    isLoading: isLoadingSiblings,
    isSuccess: isSiblingsSuccess,
  } = useGetFacilities(
    {
      "filter[parentFacilityId]": parentWorkplace?.userId ?? "",
      limit: 1000,
    },
    { enabled: isDefined(parentWorkplace) }
  );

  const {
    data: childWorkplaces,
    isLoading: isLoadingChildren,
    isSuccess: isChildrenSuccess,
  } = useGetFacilities({
    "filter[parentFacilityId]": currentWorkplace.userId,
    limit: 1000,
  });

  const linkLabel = parentWorkplace?.name ?? currentWorkplace.name;
  const hasChildren = isChildrenSuccess && childWorkplaces.length > 0;
  const siblings =
    siblingWorkplaces?.filter((workplace) => workplace.userId !== currentWorkplace.userId) ?? [];
  const hasSiblings = isSiblingsSuccess && siblings.length > 0;
  const headerWorkplace = parentWorkplace ?? currentWorkplace;
  const rowLabel = isDefined(parentWorkplace) ? "Parent Workplace" : "Corporate Hierarchy";

  if (!isDefined(parentWorkplace) && !hasChildren) {
    return undefined;
  }

  return (
    <>
      <div>
        <label style={{ display: "inline-block", minWidth: 180 }}>{rowLabel}</label>
        &nbsp;
        <Link
          component="button"
          variant="body2"
          onClick={() => {
            corporateModalState.openModal();
          }}
        >
          {linkLabel}
        </Link>
      </div>

      {corporateModalState.modalIsOpen && (
        <Dialog
          open={corporateModalState.modalIsOpen}
          onClose={() => {
            corporateModalState.closeModal();
          }}
          maxWidth="md"
          fullWidth
          scroll="paper"
          PaperProps={{ sx: { minWidth: 540 } }}
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Text variant="h3">Corporate Hierarchy</Text>

              <CloseIcon
                aria-label="Close"
                onClick={() => {
                  corporateModalState.closeModal();
                }}
              />
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Stack direction="row">
                <Text variant="subtitle2" sx={{ width: "128px" }}>
                  Name:
                </Text>
                {isDefined(parentWorkplace) ? (
                  <Link
                    component={Button}
                    onClick={() => switchToUserSession({ userId: parentWorkplace.userId })}
                    sx={{ verticalAlign: "baseline" }}
                  >
                    {parentWorkplace.name}
                  </Link>
                ) : (
                  <Text variant="subtitle2">{headerWorkplace.name}</Text>
                )}
              </Stack>
              <Stack direction="row">
                <Text variant="subtitle2" sx={{ width: "128px" }}>
                  Salesforce Id:
                </Text>
                <Text variant="subtitle2">{headerWorkplace.salesforceID}</Text>
              </Stack>

              {isDefined(parentWorkplace) && (
                <>
                  {isLoadingSiblings ? (
                    <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                  ) : undefined}
                  {isSiblingsSuccess ? (
                    <Stack>
                      <Text variant="subtitle2">Sibling Workplaces:</Text>
                      {hasSiblings ? (
                        <List sx={{ listStyleType: "disc", paddingLeft: 4 }}>
                          {siblings.map((workplace) => (
                            <ListItem
                              key={workplace.userId}
                              sx={{ display: "list-item" }}
                              disablePadding
                            >
                              <Link
                                component={Button}
                                onClick={() =>
                                  switchToUserSession({
                                    userId: workplace.userId,
                                  })
                                }
                                sx={{ verticalAlign: "baseline" }}
                              >
                                {workplace.name}
                              </Link>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Text
                          variant="body2"
                          sx={(theme) => ({
                            color: theme.palette.text.secondary,
                          })}
                        >
                          No sibling workplaces
                        </Text>
                      )}
                    </Stack>
                  ) : undefined}
                </>
              )}

              {isLoadingChildren ? (
                <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
              ) : undefined}
              {hasChildren ? (
                <Stack>
                  <Text variant="subtitle2">Child Workplaces:</Text>
                  <List sx={{ listStyleType: "disc", paddingLeft: 4 }}>
                    {childWorkplaces.map((workplace) => (
                      <ListItem key={workplace.userId} sx={{ display: "list-item" }} disablePadding>
                        <Link
                          component={Button}
                          onClick={() => switchToUserSession({ userId: workplace.userId })}
                          sx={{ verticalAlign: "baseline" }}
                        >
                          {workplace.name}
                        </Link>
                      </ListItem>
                    ))}
                  </List>
                </Stack>
              ) : undefined}
            </Stack>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
